import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ImageOff, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import PageHeader from "@/components/PageHeader";
import { createProduct, deleteProduct, errMsg, getBusiness, listProducts, updateProduct } from "@/lib/api";
import { money } from "@/lib/format";

const EMPTY = { name: "", description: "", price: "", category: "", image_url: "", available: true };

function ProductDialog({ open, onOpenChange, initial, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setF(initial ? { ...initial } : EMPTY); }, [initial, open]);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...f, price: Number(f.price || 0) };
      delete body.id; delete body.created_at;
      initial ? await updateProduct(initial.id, body) : await createProduct(body);
      toast.success(initial ? "Producto actualizado" : "Producto creado");
      onSaved();
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="product-dialog">
        <DialogHeader><DialogTitle className="font-display">{initial ? "Editar producto" : "Nuevo producto"}</DialogTitle></DialogHeader>
        <div className="grid gap-4">
          <div><Label>Nombre</Label><Input data-testid="product-name-input" value={f.name} onChange={set("name")} className="mt-1.5" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Precio</Label><Input data-testid="product-price-input" type="number" min="0" step="0.01" value={f.price} onChange={set("price")} className="mt-1.5" /></div>
            <div><Label>Categoría</Label><Input data-testid="product-category-input" value={f.category} onChange={set("category")} className="mt-1.5" placeholder="Ej. Bebidas" /></div>
          </div>
          <div><Label>Descripción</Label><Textarea data-testid="product-description-input" value={f.description} onChange={set("description")} className="mt-1.5" rows={3} /></div>
          <div><Label>URL de imagen (opcional)</Label><Input data-testid="product-image-input" value={f.image_url} onChange={set("image_url")} className="mt-1.5" placeholder="https://…" /></div>
          <label className="flex items-center gap-3 text-sm">
            <Switch data-testid="product-available-switch" checked={f.available} onCheckedChange={(v) => setF((x) => ({ ...x, available: v }))} />
            Disponible (el bot lo ofrece)
          </label>
        </div>
        <DialogFooter>
          <Button data-testid="product-save-button" onClick={save} disabled={saving || !f.name.trim()} className="rounded-full bg-emerald-500 hover:bg-emerald-600">
            {saving ? "Guardando…" : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProductCard({ p, cur, onEdit, onDelete, onToggle }) {
  return (
    <article data-testid={`product-card-${p.id}`} className="group bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-md transition-shadow animate-rise">
      <div className="aspect-[4/3] bg-slate-100 relative overflow-hidden">
        {p.image_url ? (
          <img src={p.image_url} alt={p.name} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="h-full w-full flex items-center justify-center text-slate-300"><ImageOff className="h-8 w-8" /></div>
        )}
        {!p.available && <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-white/90 text-xs font-bold text-slate-600">Agotado</span>}
      </div>
      <div className="p-4">
        <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-emerald-600">{p.category || "General"}</p>
        <div className="mt-1 flex items-start justify-between gap-2">
          <h3 className="font-display font-bold text-[#111B21] leading-tight">{p.name}</h3>
          <span className="font-display font-extrabold whitespace-nowrap" data-testid={`product-price-${p.id}`}>{money(p.price, cur)}</span>
        </div>
        {p.description && <p className="mt-1 text-sm text-slate-500 line-clamp-2">{p.description}</p>}
        <div className="mt-4 flex items-center justify-between">
          <label className="flex items-center gap-2 text-xs text-slate-500">
            <Switch data-testid={`product-toggle-${p.id}`} checked={p.available} onCheckedChange={onToggle} />Disponible
          </label>
          <div className="flex gap-1">
            <Button size="icon" variant="ghost" data-testid={`product-edit-${p.id}`} onClick={onEdit}><Pencil className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" data-testid={`product-delete-${p.id}`} onClick={onDelete} className="hover:text-rose-600"><Trash2 className="h-4 w-4" /></Button>
          </div>
        </div>
      </div>
    </article>
  );
}

export default function Catalog() {
  const [items, setItems] = useState([]);
  const [cur, setCur] = useState("USD");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => listProducts().then(setItems).catch(() => toast.error("No se pudo cargar el catálogo"));
  useEffect(() => { load(); getBusiness().then((b) => setCur(b.currency || "USD")).catch(() => {}); }, []);

  const remove = async (p) => {
    if (!window.confirm(`¿Eliminar "${p.name}"?`)) return;
    await deleteProduct(p.id); toast.success("Producto eliminado"); load();
  };
  const toggle = async (p, v) => {
    const { id, created_at, ...rest } = p;
    await updateProduct(id, { ...rest, available: v }); load();
  };

  const filtered = items.filter((p) => `${p.name} ${p.category}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="h-full overflow-y-auto scroll-thin" data-testid="catalog-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <PageHeader title="Catálogo" subtitle="Lo que pongas aquí es lo que el bot puede ofrecer y vender, con sus precios exactos.">
          <Button data-testid="add-product-button" onClick={() => { setEditing(null); setOpen(true); }} className="rounded-full bg-emerald-500 hover:bg-emerald-600">
            <Plus className="h-4 w-4 mr-1.5" />Nuevo producto
          </Button>
        </PageHeader>
        <div className="relative max-w-sm mb-6">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="catalog-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar producto o categoría" className="pl-9 bg-white rounded-full" />
        </div>
        {filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-14 text-center" data-testid="catalog-empty">
            <p className="font-display text-lg font-bold">Tu catálogo está vacío</p>
            <p className="text-sm text-slate-500 mt-1">Agrega productos o servicios para que el bot pueda venderlos.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {filtered.map((p) => (
              <ProductCard key={p.id} p={p} cur={cur} onEdit={() => { setEditing(p); setOpen(true); }} onDelete={() => remove(p)} onToggle={(v) => toggle(p, v)} />
            ))}
          </div>
        )}
      </div>
      <ProductDialog open={open} onOpenChange={setOpen} initial={editing} onSaved={load} />
    </div>
  );
}
